"use client";

import React, { createContext, useContext, useMemo } from "react";

import {
  FeatureFlagMap as FlagMap,
  FeatureFlagKey as FeatureKey,
  isKnownFeatureFlag,
} from "@/lib/feature-flags/flags";

import { useAuth } from "@/dashboard/contexts/auth-context";
import { useFeatureFlagsQuery } from "@/dashboard/hooks/feature-flags/use-feature-flags-query";

interface Ctx {
  flags: FlagMap | null;
  loading: boolean;
  hasFeature: (k: FeatureKey) => boolean;
}
const FeatureContext = createContext<Ctx>({} as Ctx);

/**
 * Parse URL parameters to extract feature flag overrides
 */
function getUrlFeatureFlagOverrides(): Partial<FlagMap> {
  if (typeof window === "undefined") {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const overrides: Partial<FlagMap> = {};

  for (const [key, value] of searchParams.entries()) {
    if (isKnownFeatureFlag(key)) {
      // Parse boolean from string (supports "true", "false", "1", "0")
      const boolValue = value === "true" || value === "1";
      overrides[key] = boolValue;
      console.log(`[feature-flags] URL override: ${key} = ${boolValue}`);
    }
  }

  return overrides;
}

export default function FeatureProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useFeatureFlagsQuery(isAuthenticated);

  // Get URL parameter overrides
  const urlOverrides = useMemo(() => getUrlFeatureFlagOverrides(), []);

  // Merge database flags with URL overrides
  const finalFlags = useMemo(() => {
    if (!data) {
      return null;
    }
    return { ...data, ...urlOverrides } as FlagMap;
  }, [data, urlOverrides]);

  const value: Ctx = {
    flags: finalFlags,
    loading: isLoading,
    hasFeature: k => !!finalFlags?.[k],
  };

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}

export const useFeatureFlags = () => {
  const ctx = useContext(FeatureContext);
  if (!ctx) {
    throw new Error("useFeatureFlags must be used within a FeatureProvider");
  }
  return ctx;
};
